/**
 * 「切换收单方」替换计划 —— A8 §7.8.2「切换收单方（后台批量动作，复用本节）」的纯计算部分。
 *
 * 输入：重读得到的策略组成员（有序）、该 App 的可选支付方式（§7.8.8）、源通道与目标通道。
 * 规则：源通道的每个**有品牌**成员，替换为目标通道同一面板品牌（`payment-method-panel-brand` 判定）的
 * **目录行**，位置不变；无品牌成员、非源通道成员原样保留；目标通道没有该品牌的可选目录行时保留。
 * 产出替换项、保留项（附原因）与最终成员集合，页面据此给运营确认，确认后以最终集合整组替换（§7.8.2 PUT）。
 *
 * 契约未单列、这里按「宁可保留、不猜」处理的两种情形（保留后若仍有同品牌冲突，由页面按 §7.8.2 拦）：
 * - 目标通道有**多个**同品牌目录行（WEB 上 `wallet` 同时对应 `applepay` 与 `googlepay`）→ 保留，不替换；
 * - 同品牌目标行已在最终成员里（WEB 上 `applepay` + `googlepay` 两个成员都映射到同一 `wallet` 行）→ 后到者保留，
 *   不产生重复成员。
 */

import type { PaymentMethodOption } from '@novel-admin/shared';
import {
  type PanelBrandPlatform,
  isSamePanelBrand,
  panelBrandKeyOf,
} from './payment-method-panel-brand';

/** 保留原因 */
export type AcquirerSwitchKeptReason =
  /** 不是源通道的成员 */
  | 'OTHER_CHANNEL'
  /** 源通道但无面板品牌（如整个供应商收银台 `PAYERMAX`） */
  | 'NO_BRAND'
  /** 目标通道没有该品牌的可选目录行 */
  | 'NO_TARGET'
  /** 目标通道有多个同品牌目录行，无法唯一确定替换目标 */
  | 'AMBIGUOUS_TARGET'
  /** 同品牌的目标行已在最终成员里 */
  | 'TARGET_ALREADY_PRESENT'
  /** 不在可选列表（已退役等），查不到通道与品牌 */
  | 'NOT_SELECTABLE';

export interface AcquirerSwitchPlan {
  /** 最终成员集合（有序，即整组替换提交的 `payment_methods`） */
  finalMembers: string[];
  /** 替换项（按成员原次序） */
  replaced: { from: string; to: string }[];
  /** 保留项（按成员原次序） */
  kept: { method: string; reason: AcquirerSwitchKeptReason }[];
}

export interface AcquirerSwitchInput {
  members: readonly string[];
  options: readonly PaymentMethodOption[];
  sourceChannel: string;
  targetChannel: string;
  platform: PanelBrandPlatform | null;
}

/** 有面板品牌的目录行才可能是替换目标（契约：替换为「目录行」，同步行不作目标） */
function isBrandedCatalogRow(option: PaymentMethodOption): boolean {
  return option.source === 'FIXED' && panelBrandKeyOf(option) !== null;
}

/** 源通道候选：当前成员里出现、且至少有一个有品牌成员的通道（按成员次序去重） */
export function acquirerSwitchSourceChannels(
  members: readonly string[],
  options: readonly PaymentMethodOption[],
): string[] {
  const byMethod = new Map(options.map((option) => [option.payment_method, option]));
  const channels: string[] = [];
  for (const member of members) {
    const option = byMethod.get(member);
    if (!option || panelBrandKeyOf(option) === null) continue;
    if (!channels.includes(option.channel)) channels.push(option.channel);
  }
  return channels;
}

/** 目标通道候选：可选列表里有品牌目录行、且不是源通道的通道（按可选列表次序去重） */
export function acquirerSwitchTargetChannels(
  options: readonly PaymentMethodOption[],
  sourceChannel: string,
): string[] {
  const channels: string[] = [];
  for (const option of options) {
    if (!isBrandedCatalogRow(option) || option.channel === sourceChannel) continue;
    if (!channels.includes(option.channel)) channels.push(option.channel);
  }
  return channels;
}

export function planAcquirerSwitch(input: AcquirerSwitchInput): AcquirerSwitchPlan {
  const byMethod = new Map(input.options.map((option) => [option.payment_method, option]));
  const targetRows = input.options.filter(
    (option) => option.channel === input.targetChannel && isBrandedCatalogRow(option),
  );
  const finalMembers = [...input.members];
  const present = new Set(input.members);
  const replaced: AcquirerSwitchPlan['replaced'] = [];
  const kept: AcquirerSwitchPlan['kept'] = [];

  input.members.forEach((member, index) => {
    const outcome = replacementOf(member);
    if (typeof outcome !== 'string') {
      kept.push({ method: member, reason: outcome.reason });
      return;
    }
    finalMembers[index] = outcome;
    present.delete(member);
    present.add(outcome);
    replaced.push({ from: member, to: outcome });
  });

  /** 单个成员的去向：替换目标取值，或保留原因 */
  function replacementOf(member: string): string | { reason: AcquirerSwitchKeptReason } {
    const option = byMethod.get(member);
    if (!option) return { reason: 'NOT_SELECTABLE' };
    if (option.channel !== input.sourceChannel) return { reason: 'OTHER_CHANNEL' };
    if (panelBrandKeyOf(option) === null) return { reason: 'NO_BRAND' };
    const matches = targetRows.filter((row) => isSamePanelBrand(option, row, input.platform));
    const [target] = matches;
    if (target === undefined) return { reason: 'NO_TARGET' };
    if (matches.length > 1) return { reason: 'AMBIGUOUS_TARGET' };
    if (present.has(target.payment_method)) return { reason: 'TARGET_ALREADY_PRESENT' };
    return target.payment_method;
  }

  return { finalMembers, replaced, kept };
}
